import React, { useEffect, useState } from "react";
import EventTreeMxGraphEditorView from "./SinglePhoenixMainModelView/MasterESD/EventTreeMxGraphEditorView";
import CircularIndeterminate from "hcla-web-frontend-primitives/app/components/primitives/Progress/CircularIndeterminate";
import { BreadcrumbsItem } from "react-breadcrumbs-dynamic";
import { useIntl } from "react-intl";
import { Grid } from "@material-ui/core";
import PhoenixApiManager from "../../Api/PhoenixApiManager";
import Button from "@material-ui/core/Button";
import ListItemAvatar from "@material-ui/core/ListItemAvatar";
import DragIndicatorIcon from "@material-ui/icons/DragIndicator";

import {
  DndContext,
  closestCenter,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

// ── localStorage helpers ──────────────────────────────────────────────────────

function loadCfOrder(modelId: string | number): number[] {
  try {
    const raw = localStorage.getItem(`cf_order_${modelId}`);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed;
  } catch {
    return [];
  }
}

function saveCfOrder(modelId: string | number, cfs: any[]): void {
  try {
    localStorage.setItem(
      `cf_order_${modelId}`,
      JSON.stringify(cfs.map((cf) => cf.id))
    );
  } catch {
    // storage unavailable — degrade silently
  }
}

// fetchedCfs: raw array of CF objects from API
// storedId:S array of ids in order from local Storage
function applyStoredOrder(fetchedCfs: any[], storedIds: number[]): any[] {
  if (storedIds.length === 0) return fetchedCfs;
  const byId = new Map(fetchedCfs.map((cf) => [cf.id, cf]));
  const ordered: any[] = [];
  for (const id of storedIds) {
    if (byId.has(id)) {
      ordered.push(byId.get(id));
      byId.delete(id);
    }
    // stale IDs (no longer in API) are silently dropped
  }
  // append newly added CFs not yet in stored order to the end of the ordered array
  for (const cf of byId.values()) {
    ordered.push(cf);
  }
  return ordered;
}

// ── SortableCFButton ──────────────────────────────────────────────────────────

interface SortableCFButtonProps {
  cf: any;
  modelId: string;
  history: any;
}

const SortableCFButton = ({ cf, modelId, history }: SortableCFButtonProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: cf.id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const handleClick = () => {
    if (isDragging) return;
    const bpDone =
      cf.progress.bp?.summary.completed / cf.progress.bp?.summary.total;
    if (bpDone) {
      history.push(`/model/${modelId}/main-analysis/${cf.crt_model}/bp`);
    } else {
      history.push(`/model/${modelId}/main-analysis/${cf.crt_model}/crt`);
    }
  };

  return (
    <div ref={setNodeRef} style={style}>
      <Button
        fullWidth
        disabled={!cf.crt_model}
        color="primary"
        variant="contained"
        style={{ borderBottom: "2px solid black", marginTop: "1em", minHeight: "40px", textTransform: "none" }}
        onClick={handleClick}
      >
        <ListItemAvatar
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          {/* drag handle */}
          <span
            {...attributes}
            {...listeners}
            style={{ cursor: "grab", display: "flex", alignItems: "center", marginRight: "6px" }}
            onClick={(e) => e.stopPropagation()}
          >
            <DragIndicatorIcon fontSize="small" />
          </span>

          {cf.crt_model && (
            <div
              style={{
                marginRight: "10px",
                display: "flex",
                alignItems: "center",
              }}
            >
              <div style={{ marginRight: "5px" }}>Progress:</div>
              <div
                style={{
                  width: "50px",
                  height: "20px",
                  border: "1px solid black",
                  position: "relative",
                }}
              >
                <div
                  style={{
                    width: `${
                      (cf.progress.bp?.summary.completed /
                        cf.progress.bp?.summary.total) *
                      100
                    }%`,
                    height: "100%",
                    backgroundColor: "green",
                  }}
                />
              </div>
              <div style={{ marginLeft: "5px" }}>
                {cf.progress.bp?.summary.completed}/{cf.progress.bp?.summary.total}
              </div>
            </div>
          )}

          <div style={{ maxWidth: "200px", wordWrap: "break-word" }}>
            <span>{cf.title}</span>
          </div>
        </ListItemAvatar>
      </Button>
    </div>
  );
};

// ── MasterESD_main ────────────────────────────────────────────────────────────

interface Props {
  details: any;
}

const MasterESD_main = (props: Props) => {
  const {
    details,
    history,
    match: {
      params: { id },
    },
  } = props;

  const [data, setData] = useState(null);
  const [criticalFunctions, setCriticalFunctions] = useState([]);
  const [activeNextButton, setActiveNextButton] = useState(false);
  // create a const for the status for criticle functions
  // const [cfStatus,setCfStatus]= useState([]);
  const [flag, setFlag] = useState(true);
  const intl = useIntl();

  const sensors = useSensors(useSensor(PointerSensor));

  useEffect(() => {
    PhoenixApiManager.getMasterEsd(id)
      .then((response) => setData(response.data))
      .then(() => {
        PhoenixApiManager.getMainCriticalFunctions(id).then((data) => {
          const storedIds = loadCfOrder(id);
          const ordered = applyStoredOrder(data.data, storedIds);
          setCriticalFunctions(ordered);
          if (data.data.next) getNextCriticalFunctions(data.data.next);
        });
      });
  }, []);

  useEffect(() => {
    const dp_completed = criticalFunctions
      .filter((cf) => cf.crt_model != null && cf.failure_probability == null)
      .map((cf) => cf.progress.dp.summary.completed >= cf.progress.dp.summary.total);
    const bp_completed = criticalFunctions
      .filter((cf) => cf.crt_model != null && cf.failure_probability == null)
      .map((cf) => cf.progress.bp.summary.completed >= cf.progress.bp.summary.total);
    setActiveNextButton(
      dp_completed.every((v) => Boolean(v)) && bp_completed.every((v) => Boolean(v))
    );
  }, [criticalFunctions]);

  const filterCriticalFunctions = (results: any[]) => {
    return results.filter((result) =>
      result.title.includes(`${id.toString()} - `)
    );
  };

  const getNextCriticalFunctions = (next: string) => {
    PhoenixApiManager.getNextCriticalFunctions(next)
      .then((res) => res.json())
      .then((data) => {
        setCriticalFunctions((prev) => [...prev, ...filterCriticalFunctions(data.results)]);
        if (data.next) getNextCriticalFunctions(data.next);
      });
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setCriticalFunctions((cfs) => {
      const oldIndex = cfs.findIndex((cf) => cf.id === active.id);
      const newIndex = cfs.findIndex((cf) => cf.id === over.id);
      const reordered = arrayMove(cfs, oldIndex, newIndex);
      saveCfOrder(id, reordered);
      return reordered;
    });
  };

  //FUNCTIONS FOR CHECKING THE API RETURN
  // const checkTrue=(cf)=>{
  // 	return cf===true;
  // }
  // const getAllCFStatus=(cf_array)=>{
  // 	return cf_array.every(checkTrue)
  // }
  // const getCriticalFunctionStatus=()=>{
  // 	PhoenixApiManager.getCriticalFunctionStatus()
  // 		.then((res)=>res.json())
  // 		.then(data=>{
  // 			setCfStatus(data.results)
  // 		})
  // }

  //remember to map with index when going through the loop and get the states from
  //cfStatus

  //TEST ONLY:
  //const cfStatus=[true,false];

  //Need Jiale to update the backend to make sure all the thing is completed
  const renderGraph = () => {
    return (
      <Grid container spacing={2} wrap="wrap-reverse" justify="flex-start">
        <Grid item xs={9}>
          <EventTreeMxGraphEditorView
            modelId={id}
            data={data}
            hide={{ transferGate: true, quantification: true }}
            setMaxHeight
          />
        </Grid>
        <Grid
          item
          xs={3}
          direction="row"
          justify="flex-end"
          alignItems="flex-end"
        >
          <span>All critical functions</span>

          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={criticalFunctions.map((cf) => cf.id)}
              strategy={verticalListSortingStrategy}
            >
              {criticalFunctions.map((cf) => (
                <SortableCFButton
                  key={cf.id}
                  cf={cf}
                  modelId={id}
                  history={history}
                />
              ))}
            </SortableContext>
          </DndContext>

          <Button
            fullWidth
            color={"primary"}
            variant={"outlined"}
            style={{ borderBottom: "2px solid black", marginTop: "2em" }}
            disabled={!activeNextButton}
            onClick={() =>
              history.push(`/model/${details.id}/main-analysis/hfe/`)
            }
          >
            Next
          </Button>
          <Button
            fullWidth
            color={"primary"}
            variant={"outlined"}
            style={{ borderBottom: "2px solid black", marginTop: "3em" }}
            onClick={() =>
              (window.location.href = `/model/${details.id}/main-overview`)
            }
          >
            Go back
          </Button>
        </Grid>
      </Grid>
    );
  };

  return (
    <React.Fragment>
      <BreadcrumbsItem to={`/model/${details.id}/main-master-esd`}>
        {intl.formatMessage({ id: "master-esd" })}
      </BreadcrumbsItem>
      {/* <Typography>Before proceeding: PLEASE connect critical functions to its corresponding endstate and be sure to double-click and states to re-label their names.</Typography> */}
        

      {data ? renderGraph() : <CircularIndeterminate size={20} />}
    </React.Fragment>
  );
};

export default MasterESD_main;
